import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Truck, Barcode, Package, FileText, Plus, MagnifyingGlass, DownloadSimple, Check
} from '@phosphor-icons/react';
import SortableTable from '../components/SortableTable';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShippingPage() {
  const [orders, setOrders] = useState([]);
  const [manifests, setManifests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [labelData, setLabelData] = useState(null);
  const [labelForm, setLabelForm] = useState({ courier: 'Default Logistics', awb_number: '' });
  const [labelOrderId, setLabelOrderId] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, manifestsRes] = await Promise.all([
        axios.get(`${API_URL}/api/orders`, { withCredentials: true }),
        axios.get(`${API_URL}/api/manifests`, { withCredentials: true }),
      ]);
      setOrders(ordersRes.data.filter(o => ['accepted', 'packed', 'shipped'].includes(o.status)));
      setManifests(manifestsRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const shippableOrders = orders.filter(o => !search ||
    o.marketplace_order_id?.toLowerCase().includes(search.toLowerCase()) ||
    o.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
    o.awb_number?.toLowerCase().includes(search.toLowerCase())
  );

  const handleGenerateLabel = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/orders/${labelOrderId}/shipping-label`, {
        courier: labelForm.courier,
        awb_number: labelForm.awb_number || undefined,
      }, { withCredentials: true });
      setLabelData(res.data);
      toast.success(`Label generated: AWB ${res.data.awb_number}`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDownloadLabel = () => {
    if (!labelData?.label_pdf_base64) return;
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${labelData.label_pdf_base64}`;
    link.download = `label_${labelData.awb_number}.pdf`;
    link.click();
  };

  const handleGenerateManifest = async () => {
    if (selectedOrders.length === 0) { toast.error('Select orders first'); return; }
    try {
      const res = await axios.post(`${API_URL}/api/orders/manifest`, {
        order_ids: selectedOrders,
      }, { withCredentials: true });
      toast.success(`Manifest ${res.data.manifest_id} generated for ${res.data.order_count} orders`);
      setSelectedOrders([]);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleOrderSelect = (orderId) => {
    setSelectedOrders(prev => prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]);
  };

  const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

  const orderColumns = [
    { key: 'order_select', label: '', sortable: false, render: (v, row) => (
      <input type="checkbox" checked={selectedOrders.includes(row.order_id)} onChange={() => toggleOrderSelect(row.order_id)} className="w-4 h-4 rounded border-[#e5e5ea]" data-testid={`select-order-${row.order_id}`} />
    )},
    { key: 'marketplace_order_id', label: 'Order ID', render: v => <span className="font-mono text-sm font-medium">{v}</span> },
    { key: 'customer_name', label: 'Customer' },
    { key: 'total_amount', label: 'Amount', render: v => fmt(v) },
    { key: 'status', label: 'Status', render: v => {
      const c = v === 'shipped' ? '#28cd8c' : v === 'packed' ? '#3b82f6' : '#f6a609';
      return <span className="px-2 py-0.5 text-[11px] font-bold rounded capitalize" style={{ backgroundColor: `${c}15`, color: c }}>{v}</span>;
    }},
    { key: 'awb_number', label: 'AWB', render: v => v ? <span className="font-mono text-xs bg-[#f7f7fa] px-2 py-1 rounded">{v}</span> : <span className="text-[#9c9db7] text-xs">Not assigned</span> },
    { key: 'courier', label: 'Courier', render: v => v || '-' },
    { key: 'order_actions', label: 'Actions', sortable: false, render: (v, row) => (
      <div className="flex gap-1">
        {!row.label_generated ? (
          <Button size="sm" className="bg-[#7d64ff] hover:bg-[#6153a7] text-white text-xs" onClick={() => {
            setLabelOrderId(row.order_id);
            setLabelForm({ courier: 'Default Logistics', awb_number: '' });
            setLabelData(null);
            setLabelDialogOpen(true);
          }} data-testid={`gen-label-${row.order_id}`}>
            <Barcode size={14} className="mr-1" /> Label
          </Button>
        ) : (
          <span className="text-xs text-[#28cd8c] font-semibold flex items-center gap-1"><Check size={14} /> Generated</span>
        )}
      </div>
    )}
  ];

  const manifestColumns = [
    { key: 'manifest_id', label: 'Manifest ID', render: v => <span className="font-mono text-sm font-bold">{v}</span> },
    { key: 'order_count', label: 'Orders' },
    { key: 'status', label: 'Status', render: v => <span className="px-2 py-0.5 text-xs font-semibold rounded sp-badge-success capitalize">{v}</span> },
    { key: 'created_at', label: 'Created', render: v => new Date(v).toLocaleString() },
  ];

  return (
    <DashboardLayout title="Shipping & Labels" subtitle="Generate shipping labels and manifests">
      {/* Action Bar */}
      <div className="bg-white border border-[#e5e5ea] mb-6">
        <div className="action-bar">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5a5c87]" size={18} />
            <Input placeholder="Search by Order ID, Customer, AWB..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-sm" data-testid="shipping-search" />
          </div>
          {selectedOrders.length > 0 && (
            <Button onClick={handleGenerateManifest} className="bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="generate-manifest-btn">
              <FileText size={16} className="mr-1" /> Generate Manifest ({selectedOrders.length})
            </Button>
          )}
        </div>
      </div>

      {/* Orders ready for shipping */}
      <div className="mb-6">
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#5a5c87] mb-3">Orders Ready for Shipping</h3>
        {loading ? (
          <div className="loading-spinner"><div className="spinner"></div></div>
        ) : (
          <SortableTable columns={orderColumns} data={shippableOrders} pageSize={15} emptyMessage="No orders ready for shipping" emptyIcon={Package} />
        )}
      </div>

      {/* Manifests */}
      {manifests.length > 0 && (
        <div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#5a5c87] mb-3">Generated Manifests</h3>
          <SortableTable columns={manifestColumns} data={manifests} pageSize={10} emptyMessage="No manifests" emptyIcon={FileText} />
        </div>
      )}

      {/* Label Generation Dialog */}
      <Dialog open={labelDialogOpen} onOpenChange={setLabelDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Generate Shipping Label</DialogTitle></DialogHeader>
          {!labelData ? (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Courier Partner</label>
                <Select value={labelForm.courier} onValueChange={v => setLabelForm({ ...labelForm, courier: v })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="label-courier-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Default Logistics">Default Logistics</SelectItem>
                    <SelectItem value="Delhivery">Delhivery</SelectItem>
                    <SelectItem value="BlueDart">BlueDart</SelectItem>
                    <SelectItem value="DTDC">DTDC</SelectItem>
                    <SelectItem value="Ecom Express">Ecom Express</SelectItem>
                    <SelectItem value="Shadowfax">Shadowfax</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">AWB Number (optional - auto-generated if empty)</label>
                <Input value={labelForm.awb_number} onChange={e => setLabelForm({ ...labelForm, awb_number: e.target.value })} placeholder="Auto-generated" className="mt-1 rounded-sm" data-testid="label-awb-input" />
              </div>
              <div className="flex gap-4 pt-2">
                <Button variant="outline" onClick={() => setLabelDialogOpen(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleGenerateLabel} className="flex-1 bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="generate-label-btn">Generate Label</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-[#f7f7fa] rounded-lg border border-[#e5e5ea]">
                <div className="flex items-center gap-2 mb-3">
                  <Check size={20} className="text-[#28cd8c]" weight="bold" />
                  <span className="font-semibold text-[#28cd8c]">Label Generated Successfully</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-[#9c9db7] text-xs uppercase">AWB</span><p className="font-mono font-bold">{labelData.awb_number}</p></div>
                  <div><span className="text-[#9c9db7] text-xs uppercase">Courier</span><p className="font-medium">{labelData.courier}</p></div>
                </div>
              </div>
              <div className="flex gap-4">
                {labelData.label_pdf_base64 && (
                  <Button onClick={handleDownloadLabel} className="flex-1 bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="download-label-btn">
                    <DownloadSimple size={16} className="mr-1" /> Download Label PDF
                  </Button>
                )}
                <Button variant="outline" onClick={() => setLabelDialogOpen(false)} className="flex-1">Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
